import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { Temporal } from 'temporal-polyfill';

import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { getSubSessionTeachers } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import {
  getCoachAttendanceGradeLabel,
  getCoachOverallGradeColor,
  getCoachOverallGradeLabel,
  getOverallGradeIndex,
} from '@/lib/coach-utils';
import { BackgroundCheckStatusBadgeList } from '@/routes/_layout/teachers/-components/background-check-status-badge-list';
import { useMemo, useState } from 'react';
import { GetSubSessionTeachersResults } from '@airreading/common/data';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';

type SubSessionTeacher = GetSubSessionTeachersResults['coaches'][number];

interface SubSessionTeachersTableProps {
  sessionId: string;
  studentGroupId: string;
  startDate: Temporal.PlainDate;
  endDate: Temporal.PlainDate;
  previousTeacherIds: string[];
  onCancel: () => void;
  onSelect: (sendConfirmationEmail: boolean, teacherId: string, teacherName: string) => void;
}

export function SubSessionTeachersTable({
  studentGroupId,
  startDate,
  endDate,
  previousTeacherIds,
  onCancel,
  onSelect,
}: SubSessionTeachersTableProps) {
  const [selectedTeacher, setSelectedTeacher] = useState<{ teacherId: string; teacherName: string } | null>(null);
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(true);

  const columnHelper = createColumnHelper<SubSessionTeacher>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: '',
        cell: ({ row }) => (
          <input
            type="radio"
            name="teacher-select"
            checked={selectedTeacher?.teacherId === row.original.id}
            onChange={() =>
              setSelectedTeacher({ teacherId: row.original.id, teacherName: row.original.fullNameOrEmail })
            }
            aria-label="Select teacher"
            className="cursor-pointer accent-black"
          />
        ),
        size: 50,
      }),
      columnHelper.display({
        id: 'previouslyTaught',
        header: 'Previously taught',
        cell: ({ row }) => {
          const hasTaught = previousTeacherIds.includes(row.original.id);
          return hasTaught ? (
            <span className="text-black font-medium">✅ Yes</span>
          ) : (
            <span className="text-black">❌ No</span>
          );
        },
        size: 150,
      }),
      columnHelper.accessor('fullNameOrEmail', {
        id: 'fullNameOrEmail',
        header: 'Teacher name',
        cell: ({ row, getValue }) => (
          <Link to={'/teachers/$teacherId'} params={{ teacherId: row.original.id }}>
            {getValue()}
          </Link>
        ),
      }),
      columnHelper.accessor((row) => row, {
        id: 'teacherGrade',
        header: 'Teacher grade',
        cell: ({ getValue }) => {
          const { qualityGrade } = getValue();
          return qualityGrade;
        },
      }),

      columnHelper.accessor((row) => row, {
        id: 'qualityGrade',
        header: 'Attendance grade',
        cell: ({ getValue }) => {
          const { attendanceGrade } = getValue();
          return getCoachAttendanceGradeLabel(attendanceGrade);
        },
      }),

      columnHelper.accessor('overallGrade', {
        id: 'overallGrade',
        header: 'Overall Grade',
        cell: ({ getValue }) => {
          const overallGrade = getValue();
          const color = getCoachOverallGradeColor(overallGrade);
          if (color)
            return (
              <span
                className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xl font-bold`}
                style={{ backgroundColor: color }}
              >
                {getCoachOverallGradeLabel(overallGrade)}
              </span>
            );
          return getCoachOverallGradeLabel(overallGrade);
        },
      }),

      columnHelper.accessor('backgroundCheckStatuses', {
        id: 'backgroundCheckStatuses',
        header: 'Background Check',
        cell: ({ getValue }) => {
          const statuses = getValue();
          return statuses && statuses.length > 0 ? (
            <div className="max-w-lg">
              <BackgroundCheckStatusBadgeList statuses={statuses} />
            </div>
          ) : (
            '-'
          );
        },
      }),

      columnHelper.accessor((row) => row, {
        id: 'weeklySubCount',
        header: 'Weekly subs count',
        cell: ({ getValue }) => {
          const { weeklySubsCount } = getValue();
          return weeklySubsCount;
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTeacher],
  );

  const {
    data: coachesResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getSubSessionTeachers({
      studentGroupId,
      startDate,
      endDate,
    }),
  );

  // Sort coaches by: Previously taught > Overall Grade > Weekly subs count
  const coaches = (coachesResp?.coaches || []).slice().sort((a, b) => {
    // 1) Previously taught: yes (true) first, then no (false)
    const aTaught = previousTeacherIds.includes(a.id);
    const bTaught = previousTeacherIds.includes(b.id);
    if (aTaught !== bTaught) {
      return aTaught ? -1 : 1;
    }

    // 2) Overall grade: higher first
    const aIndex = getOverallGradeIndex(a.overallGrade);
    const bIndex = getOverallGradeIndex(b.overallGrade);
    if (aIndex !== bIndex) return aIndex - bIndex;

    // 3) Weekly subs count: higher first
    return (b.weeklySubsCount || 0) - (a.weeklySubsCount || 0);
  });

  useErrorToast(error, 'Error loading available teachers. Please try again.');

  const table = useReactTable({
    data: coaches,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: coaches.length,
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <>
      <EntityTable
        table={table}
        emptyState="No teachers found for this sub session"
        errorState={<TableErrorState refetchAction={refetch} />}
        hasError={!!error}
        isLoading={isPending}
        showPagination={false}
        emptyStateClassName="translate-y-0"
        hideEndMessage
      />
      <div className="flex justify-between items-center gap-2 mt-4">
        <div className="flex items-center">
          <Checkbox
            id="send-confirmation-email"
            checked={sendConfirmationEmail}
            onCheckedChange={(checked) => setSendConfirmationEmail(!!checked)}
          />
          <label htmlFor="send-confirmation-email" className="cursor-pointer pl-2">
            Send confirmation email
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              selectedTeacher &&
              onSelect?.(sendConfirmationEmail, selectedTeacher.teacherId, selectedTeacher.teacherName)
            }
            disabled={!selectedTeacher}
          >
            Select
          </Button>
        </div>
      </div>
    </>
  );
}
